import React, { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { AxiosError } from 'axios'
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { CLARITY_USER_ID_STORAGE_KEY } from '@/shared/clarity-session-storage-keys'
import { USER_SESSION_QUERY_KEY } from '@/shared/hooks/api/auth'
import '@/shared/i18n'
import * as Sentry from '@sentry/react'
import { clarity } from 'react-microsoft-clarity'
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType,
} from 'react-router-dom'
import { toast } from 'sonner'
import { handleServerError } from '@/shared/lib/handle-server-error'
import ErrorPage from '@/app/ErrorPage'
import Root from '@/app/Root'
import { DirectionProvider } from '@/context/direction-provider'
import { FontProvider } from '@/context/font-provider'
import { ThemeProvider } from '@/context/theme-provider'
import '@/styles/index.css'

if (import.meta.env.VITE_CLARITY_PROJECT_ID) {
  clarity.init(import.meta.env.VITE_CLARITY_PROJECT_ID)
  const storedUserId = sessionStorage.getItem(CLARITY_USER_ID_STORAGE_KEY)
  if (storedUserId && clarity.hasStarted()) {
    clarity.identify(storedUserId, {})
  }
}

function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT
  if (!dsn || !environment) {
    return
  }
  const enableReplay = import.meta.env.VITE_SENTRY_REPLAY_ENABLED === 'true'
  const integrations = [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ]
  if (enableReplay) {
    integrations.push(Sentry.replayIntegration())
  }
  Sentry.init({
    dsn,
    environment,
    integrations,
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  })
}

initSentry()

function isAuthOrOnboardingPath(pathname: string): boolean {
  return [
    '/sign-in',
    '/signup',
    '/sign-up',
    '/forgot-password',
    '/reset-password',
    '/onboarding',
  ].some((path) => pathname === path || pathname.startsWith(`${path}/`))
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (failureCount >= 0 && import.meta.env.DEV) {
          return false
        }
        if (failureCount > 3 && import.meta.env.PROD) {
          return false
        }
        return !(
          error instanceof AxiosError && [401, 403].includes(error.response?.status ?? 0)
        )
      },
      refetchOnWindowFocus: import.meta.env.PROD,
      staleTime: 10 * 1000,
    },
    mutations: {
      onError: (error) => {
        if (error instanceof AxiosError && error.response?.status === 409) {
          return
        }
        handleServerError(error)
        if (error instanceof AxiosError && error.response?.status === 304) {
          toast.error('Content not modified!')
        }
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          toast.error('Session expired!')
          queryClient.removeQueries({ queryKey: [USER_SESSION_QUERY_KEY] })
          const pathname = window.location.pathname
          if (!isAuthOrOnboardingPath(pathname)) {
            const redirect = `${window.location.pathname}${window.location.search}${window.location.hash}`
            window.location.assign(`/sign-in?redirect=${encodeURIComponent(redirect)}`)
          } else {
            window.location.assign('/sign-in')
          }
        }
        if (error.response?.status === 500) {
          toast.error('Internal Server Error!')
        }
      }
    },
  }),
})

const createBrowserRouterWithSentry =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)
const router = createBrowserRouterWithSentry([
  {
    path: '/*',
    element: <Root />,
    errorElement: <ErrorPage />,
  },
])

const rootElement = document.getElementById('root')
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <FontProvider>
            <DirectionProvider>
              <RouterProvider router={router} />
            </DirectionProvider>
          </FontProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </StrictMode>
  )
}
